import { FC, useEffect, useRef, useState } from 'react';

import { ONEID_READY_EVENT_TYPE } from '@/lib/constants';
import {
  ONELOG_VENDOR_ID,
  whenTcfConsentAvailableForVendor,
} from '@/lib/tcf-consent';
import type { OneIdConfigResponse } from '@/pages/api/config/oneid';

const OneIdScript: FC = () => {
  const loadedRef = useRef(false);
  const initRef = useRef(false);
  const [config, setConfig] = useState<OneIdConfigResponse | null>(null);

  useEffect(() => {
    let cancelled = false;
    fetch('/api/config/oneid')
      .then((res) =>
        res.ok ? res.json() : Promise.reject(new Error('Not ok')),
      )
      .then((data: OneIdConfigResponse) => {
        if (!cancelled) setConfig(data);
      })
      .catch(() => {
        if (!cancelled) setConfig({ scriptUrl: null, firstPartyUrl: null });
      });

    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    const noopCleanup = () => undefined;

    if (typeof window === 'undefined' || !config || loadedRef.current) {
      return noopCleanup;
    }

    const scriptUrl = config.scriptUrl ?? null;
    const firstPartyUrl = config.firstPartyUrl ?? null;

    if (!scriptUrl) {
      return noopCleanup;
    }

    let cancelled = false;
    let scriptElement: HTMLScriptElement | null = null;

    whenTcfConsentAvailableForVendor(ONELOG_VENDOR_ID)
      .then(() => {
        if (cancelled || loadedRef.current) return;
        loadedRef.current = true;

        const script = document.createElement('script');
        script.src = scriptUrl;
        script.async = true;
        script.onerror = () => {
          console.warn('OneID script failed to load');
        };
        script.onload = () => {
          if (cancelled || initRef.current) return;
          try {
            const sdk = window.OneIdSDK;
            const initOptions = firstPartyUrl
              ? { firstPartyURL: firstPartyUrl }
              : undefined;
            const onReady = () => {
              initRef.current = true;
              document.dispatchEvent(new Event(ONEID_READY_EVENT_TYPE));
            };

            if (sdk?.firstParty?.init) {
              sdk.firstParty.init(initOptions, onReady);
            } else {
              const queue = (window as { OneIdSDK?: unknown[] }).OneIdSDK;

              if (Array.isArray(queue)) {
                queue.push(() => {
                  window.OneIdSDK?.firstParty?.init(initOptions, onReady);
                });
              } else {
                onReady();
              }
            }
          } catch (error) {
            console.warn('OneID SDK init error:', error);
          }
        };
        scriptElement = script;
        document.body.appendChild(script);
      })
      .catch((error: unknown) => {
        console.warn('OneID consent gate failed:', error);
      });

    return () => {
      cancelled = true;

      if (scriptElement?.parentNode) {
        scriptElement.parentNode.removeChild(scriptElement);
      }
    };
  }, [config]);

  return null;
};

export default OneIdScript;
